import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Crown, Send, RotateCw, Phone, Star, Award } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { DonnaOnboarding } from "@/components/DonnaOnboarding";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", phone: "", email: "", address: "" };

function PassBadge({ c }) {
  if (!c.pass_actif) {
    return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Inactif</span>;
  }
  if (c.pass_expired) {
    return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-600">Expiré</span>;
  }
  const soon = c.pass_days_left <= 7;
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${soon ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
      <Crown className="h-3 w-3" /> Actif · J-{c.pass_days_left}
    </span>
  );
}

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => { api.get("/clients").then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ name: c.name, phone: c.phone, email: c.email || "", address: c.address || "" });
    setEditId(c.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Nom et téléphone requis"); return; }
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post("/clients", form);
      toast.success(editId ? "Client modifié" : "Client ajouté");
      setOpen(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => { await api.delete(`/clients/${id}`); toast.success("Client supprimé"); load(); };

  const togglePass = async (c) => {
    setBusy(c.id);
    try {
      if (c.pass_actif && !c.pass_expired) {
        await api.post(`/clients/${c.id}/deactivate-pass`);
        toast.success("Pass désactivé");
      } else {
        await api.post(`/clients/${c.id}/activate-pass`);
        toast.success("Pass Privilège activé · +2 000 FCFA en Adhésions Club", { icon: "👑" });
      }
      load();
    } catch { toast.error("Erreur"); } finally { setBusy(null); }
  };

  const renew = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/clients/${c.id}/renew-pass`);
      toast.success("Adhésion renouvelée (+90 jours)");
      load();
    } catch { toast.error("Erreur"); } finally { setBusy(null); }
  };

  const sendReminder = async (c) => {
    setBusy(c.id);
    try {
      const res = await api.post(`/donna/send/${c.id}`);
      if (res.data.sent) toast.success(`Relance envoyée à ${c.name}`);
      else if (res.data.configured === false) toast.warning("WhatsApp non configuré", { description: res.data.message });
      else toast.error(res.data.error || "Échec de l'envoi WhatsApp");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec");
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in-up" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Base clients</p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Clients</h1>
        </div>
        <div className="flex items-center gap-2">
          <DonnaOnboarding onDone={load} />
          <button data-testid="add-client-button" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold text-sm transition-colors duration-200">
            <Plus className="h-4 w-4" /> Nouveau client
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 px-6 py-12 text-center text-slate-400">
            Aucun client pour l'instant.
          </div>
        )}
        {items.map((c) => {
          const active = c.pass_actif && !c.pass_expired;
          return (
            <div
              key={c.id}
              data-testid={`client-card-${c.id}`}
              className={`bg-white rounded-lg border p-5 transition-colors duration-200 ${active ? "border-amber-200 bg-amber-50/30" : "border-slate-200"}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
                    {c.member_no && (
                      <span data-testid={`member-no-${c.id}`} className="text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 tracking-wide">
                        {c.member_no}
                      </span>
                    )}
                    <PassBadge c={c} />
                    <span data-testid={`points-badge-${c.id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-sky-400 text-sky-400" /> {c.points_balance || 0} pts
                    </span>
                    {c.is_vip && (
                      <span data-testid={`vip-badge-${c.id}`} className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 inline-flex items-center gap-1">
                        <Award className="h-3 w-3" /> VIP
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" /> {c.phone}
                    {c.email && <span className="text-slate-300">·</span>}
                    {c.email && <span>{c.email}</span>}
                  </p>
                  {c.pass_actif && (
                    <div className="mt-3 flex gap-6 text-xs">
                      <div>
                        <span className="text-slate-400 uppercase tracking-wide font-semibold">Début</span>
                        <p className="text-slate-700 font-medium tabular">{formatDate(c.pass_start_date)}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wide font-semibold">Fin</span>
                        <p className="text-slate-700 font-medium tabular">{formatDate(c.pass_end_date)}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase tracking-wide font-semibold">Dernière relance</span>
                        <p className="text-slate-700 font-medium tabular" data-testid={`last-reminder-${c.id}`}>{c.reminder_sent_at ? formatDate(c.reminder_sent_at) : "—"}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600">Pass Actif</span>
                    <Switch
                      data-testid={`toggle-pass-${c.id}`}
                      checked={active}
                      disabled={busy === c.id}
                      onCheckedChange={() => togglePass(c)}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    {c.pass_actif && (c.pass_expired || c.pass_days_left <= 7) && (
                      <>
                        <button data-testid={`renew-${c.id}`} onClick={() => renew(c)} disabled={busy === c.id} title="Renouveler" className="p-2 rounded-md hover:bg-amber-100 text-amber-600 transition-colors duration-150">
                          <RotateCw className="h-4 w-4" />
                        </button>
                        <button data-testid={`whatsapp-${c.id}`} onClick={() => sendReminder(c)} disabled={busy === c.id} title="Relancer via WhatsApp" className="p-2 rounded-md hover:bg-emerald-100 text-emerald-600 transition-colors duration-150">
                          <Send className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="p-2 rounded-md hover:bg-slate-100 text-slate-500 transition-colors duration-150">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c.id)} className="p-2 rounded-md hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors duration-150">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifier le client" : "Nouveau client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nom complet</label>
              <input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Téléphone (WhatsApp)</label>
              <input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+225 07 00 00 00 00" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Adresse</label>
              <input data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
            <button data-testid="save-client-button" onClick={save} className="px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-semibold transition-colors duration-200">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
